import { useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { formatDisplayDateTime } from '../../utils/format';
import { useQueryClient } from '@tanstack/react-query';
import { useAppAccessHistory } from '../../hooks/useAppAccess';
import { useConfirmManualStep } from '../../hooks/useWorkflows';
import { invalidateMany } from '../../hooks/queryUtils';
import { AppAccessStatusChip } from './AppAccessStatusChip';
import { Button } from '../common/Button';
import { Spinner } from '../common/Spinner';
import { StatusBadge } from '../workflow/StatusBadge';
import {
  SYSTEM_DISPLAY_NAMES,
  type AppAccessSystem,
  type AppAccessTier,
  type MatrixCell,
} from '../../services/api/appAccess';

// Local mirror of WorkflowStepList's MANUAL_STEP_CONFIG — J80-04: do NOT export from WorkflowStepList.
const MANUAL_STEP_CONFIG: Record<string, { buttonText: string }> = {
  'claude-removal': { buttonText: 'Confirm removed from Claude' },
  'claude-seat-approval': { buttonText: 'Approve Claude seats' },
};

export interface AppAccessDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: { id: number; firstName: string | null; lastName: string | null; email: string };
  system: AppAccessSystem;
  cell: MatrixCell;
  originatingButtonRef: React.RefObject<HTMLButtonElement>;
  onGrant: (tier?: AppAccessTier) => void;
  onRetry: () => void;
  onRevokeRequest: () => void;
}

export function AppAccessDrawer({
  isOpen,
  onClose,
  user,
  system,
  cell,
  originatingButtonRef,
  onGrant,
  onRetry,
  onRevokeRequest,
}: AppAccessDrawerProps) {
  const [claudeTier, setClaudeTier] = useState<AppAccessTier>('claude-standard');
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Hooks must be unconditional — placed before the isOpen guard below.
  const queryClient = useQueryClient();
  const confirmMutation = useConfirmManualStep(user.id);

  // Slide-in animation: keep panel mounted, drive transform via state.
  // RESEARCH §7 / UI-SPEC §"Drawer: AppAccessDrawer / Slide animation": panel starts at translate-x-full
  // then mounts -> requestAnimationFrame -> translate-x-0 (in). On close, this component returns null
  // immediately (parent unmounts via isOpen=false); per RESEARCH §7 the simpler choice is in-only.
  // Using requestAnimationFrame (NOT useLayoutEffect) per the fix-hint to avoid layout thrash.
  const [isVisible, setIsVisible] = useState(false);

  const systemDisplayName = SYSTEM_DISPLAY_NAMES[system];
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;

  // History fetch — gated by isOpen so closed drawer doesn't refetch; polls while cell is pending.
  const historyQuery = useAppAccessHistory(
    isOpen ? user.id : null,
    isOpen ? system : null,
    isOpen && cell.status === 'pending',
  );

  // Find the waiting manual step from the latest run (J80-01, J80-02).
  const waitingStep = historyQuery.data?.[0]?.steps?.find(
    (s) => s.name in MANUAL_STEP_CONFIG && s.status === 'running',
  );

  // Slide-in: on open, flip isVisible on the next animation frame so transform transitions from
  // translate-x-full to translate-x-0. On close, reset immediately (parent unmounts via isOpen=false).
  useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
      return;
    }
    const id = window.requestAnimationFrame(() => setIsVisible(true));
    return () => window.cancelAnimationFrame(id);
  }, [isOpen]);

  // Esc handler (document-level — drawer panel is the topmost mounted overlay until modal opens on top)
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen, onClose]);

  // On open: focus the close button. On close: return focus to originating cell button.
  useEffect(() => {
    if (isOpen) {
      // Defer to allow drawer to mount + paint
      const id = setTimeout(() => closeButtonRef.current?.focus(), 0);
      return () => clearTimeout(id);
    } else {
      originatingButtonRef.current?.focus();
      return undefined;
    }
  }, [isOpen, originatingButtonRef]);

  // Focus trap — Tab/Shift+Tab cycle within drawer panel
  useEffect(() => {
    if (!isOpen) return;
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const tabbables = panelRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!tabbables || tabbables.length === 0) return;
      const first = tabbables[0] as HTMLElement;
      const last = tabbables[tabbables.length - 1] as HTMLElement;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const isAutoManaged = cell.source === 'auto' || cell.source === 'scim';
  const autoManagedMechanism = cell.source === 'scim' ? 'SCIM' : 'SSO';

  return (
    <>
      {/* Backdrop */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-40 bg-black/30"
        aria-hidden="true"
        onClick={handleOverlayClick}
      />
      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${systemDisplayName} access — ${fullName}`}
        className={`fixed inset-y-0 right-0 z-50 flex flex-col bg-white shadow-xl w-full sm:max-w-[440px] overflow-y-auto transition-transform duration-300 ease-in-out ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{systemDisplayName}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {fullName} · {user.email}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close drawer"
            className="ml-4 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Status bar */}
        <div className="border-b border-gray-100 px-6 py-3 flex items-center gap-3">
          <AppAccessStatusChip status={cell.status} source={cell.source} />
          <span className="text-xs text-gray-400">
            {cell.lastDispatchedAt
              ? `Last dispatched ${formatDisplayDateTime(cell.lastDispatchedAt)}`
              : 'No dispatch yet'}
            {' by '}
            {cell.lastChangedBy ? `User #${cell.lastChangedBy}` : 'system'}
          </span>
        </div>

        {/* Action area — state-aware (D-02) */}
        <div className="border-b border-gray-100 px-6 py-4">
          {isAutoManaged ? (
            <p className="text-sm text-gray-600">
              Managed automatically via {autoManagedMechanism}. {systemDisplayName} access cannot be
              granted or revoked manually.
            </p>
          ) : cell.status === 'not_granted' || (cell.status === 'failed' && system === 'claude') ? (
            <div className="space-y-3">
              {system === 'claude' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Tier
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setClaudeTier('claude-standard')}
                      aria-pressed={claudeTier === 'claude-standard'}
                      className={`px-3 py-2 rounded-full border text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        claudeTier === 'claude-standard'
                          ? 'border-2 border-primary-600 bg-white text-primary-700'
                          : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      Standard
                    </button>
                    <button
                      type="button"
                      onClick={() => setClaudeTier('claude-premium')}
                      aria-pressed={claudeTier === 'claude-premium'}
                      className={`px-3 py-2 rounded-full border text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        claudeTier === 'claude-premium'
                          ? 'border-2 border-primary-600 bg-white text-primary-700'
                          : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      Premium
                    </button>
                  </div>
                  <div className="mt-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded p-2">
                    <strong>Before granting:</strong> confirm enough Claude seats (Standard / Premium)
                    are available in the Claude admin console. JIT provisioning will consume a seat
                    on first sign-in.
                  </div>
                </div>
              )}
              <Button variant="primary" onClick={() => onGrant(system === 'claude' ? claudeTier : undefined)}>
                Grant access
              </Button>
            </div>
          ) : cell.status === 'granted' || cell.status === 'assumed_granted' ? (
            <Button variant="danger" onClick={onRevokeRequest}>
              Revoke access
            </Button>
          ) : cell.status === 'pending' ? (
            <div className="space-y-2">
              {waitingStep ? (
                <div className="space-y-2">
                  <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                    <strong>Manual action required.</strong> This workflow is paused waiting for
                    confirmation.
                  </div>
                  <Button
                    variant="primary"
                    isLoading={confirmMutation.isPending}
                    disabled={confirmMutation.isPending}
                    onClick={() =>
                      confirmMutation.mutate(waitingStep.id, {
                        onSuccess: () =>
                          invalidateMany(queryClient, [
                            ['app-access-matrix'],
                            ['app-access-history', user.id, system],
                          ]),
                      })
                    }
                  >
                    {MANUAL_STEP_CONFIG[waitingStep.name]!.buttonText}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Dispatch in flight — waiting for N8N callback.</p>
              )}
              {historyQuery.data?.[0]?.n8nExecutionUrl ? (
                <a
                  href={historyQuery.data[0].n8nExecutionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:text-primary-700 underline"
                >
                  View in N8N
                </a>
              ) : (
                <span className="text-xs text-gray-400">(N8N execution link not yet available)</span>
              )}
            </div>
          ) : cell.status === 'failed' ? (
            <div>
              <Button variant="primary" onClick={onRetry}>
                Retry access
              </Button>
              {cell.lastFailureReason && (
                <p className="mt-2 text-xs text-red-600">{cell.lastFailureReason}</p>
              )}
            </div>
          ) : null}
        </div>

        {/* History list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">History</h3>
          {historyQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="sm" />
            </div>
          ) : !historyQuery.data || historyQuery.data.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No prior runs for this cell.</p>
          ) : (
            <div role="list">
              {historyQuery.data.map((run) => (
                <div
                  key={run.id}
                  role="listitem"
                  className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0"
                >
                  <time
                    dateTime={run.createdAt}
                    className="text-xs text-gray-400 w-32 shrink-0"
                  >
                    {formatDisplayDateTime(run.createdAt)}
                  </time>
                  <span className="text-xs font-semibold text-gray-700 capitalize w-14 shrink-0">
                    {run.appAccessDirection ?? run.type}
                  </span>
                  <StatusBadge state={run.status} size="sm" />
                  {run.bulkOperationId && (
                    <span
                      className="inline-flex items-center rounded-full bg-purple-50 border border-purple-200 px-2 py-0.5 text-xs font-semibold text-purple-700"
                      title={`Part of a bulk ${run.appAccessDirection ?? 'dispatch'} (op ${run.bulkOperationId.slice(0, 6)})`}
                    >
                      Bulk #{run.bulkOperationId.slice(0, 6)}
                    </span>
                  )}
                  <span className="text-xs text-gray-400 truncate flex-1">
                    {run.triggeredBy !== null ? `User #${run.triggeredBy}` : 'system'}
                  </span>
                  {run.n8nExecutionUrl ? (
                    <a
                      href={run.n8nExecutionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary-600 hover:text-primary-700 whitespace-nowrap shrink-0"
                    >
                      View in N8N
                    </a>
                  ) : (
                    <span className="text-xs text-gray-400 shrink-0">(not yet available)</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
